"use client";

import { ToggleButton } from "@/components/ui/toggle-button/toggle-button";
import { faPlus } from "@fortawesome/pro-solid-svg-icons/faPlus";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "@mantine/core";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { type PropsWithChildren, useCallback } from "react";
import { twMerge } from "tailwind-merge";

import { Header } from "@/components/header";
import { ImpersonationBanner } from "@/components/impersonationBanner";
import { TableHeader } from "@/components/table/tableHeader";
import { useTrackAnalytics } from "@/hooks/analytics.hooks";
import { useScreenerBuilderEnabled } from "@/hooks/screener-builder-hooks";
import { FEATURE_FLAGS, IfFeatureEnabled } from "@/utils/growthbook";
import { buildTestId } from "@/utils/helpers";

const SHARED_TOGGLE_BUTTON_PROPS = {
  className: twMerge([
    "data-[active=true]:font-bold",
    "px-2.5",
    "py-1",
    "w-[86px]",
  ]),
  component: Link,
};

export default function ScreenersLayout({ children }: PropsWithChildren) {
  const pathname = usePathname();
  const track = useTrackAnalytics();
  const screenerBuilderEnabled = useScreenerBuilderEnabled();
  const viewingArchived = pathname.includes("archived");

  const getSubHeaderTestId = useCallback(
    (element: string) =>
      buildTestId(
        `${viewingArchived ? "archived" : "active"}-screeners`,
        "sub-header",
        element,
      ),
    [viewingArchived],
  );

  return (
    <div className="w-full h-dvh">
      <Header />
      <div>
        <TableHeader
          breadCrumbs={[{ text: "All Interviewers" }]}
          centerContent={
            <IfFeatureEnabled
              feature={FEATURE_FLAGS.viewRestoreArchivedScreeners}
            >
              <ToggleButton<typeof Link>
                compact
                leftActive={!viewingArchived}
                leftButtonProps={{
                  ...SHARED_TOGGLE_BUTTON_PROPS,
                  "data-testid": getSubHeaderTestId("active-tab"),
                  href: "/",
                  onClick: () => track("Active Screeners Tab Clicked"),
                }}
                leftContent="Active"
                rightButtonProps={{
                  ...SHARED_TOGGLE_BUTTON_PROPS,
                  "data-testid": getSubHeaderTestId("archived-tab"),
                  href: "/archived",
                  onClick: () => track("Archived Screeners Tab Clicked"),
                }}
                rightContent="Archived"
              />
            </IfFeatureEnabled>
          }
          rightContent={
            screenerBuilderEnabled && (
              <Button
                className="text-xs uppercase"
                component={Link}
                data-testid={getSubHeaderTestId("new-screener")}
                href="/screener-builder/new/position"
                onClick={() => track("New Screener Clicked")}
                rightSection={<FontAwesomeIcon icon={faPlus} />}
                variant="transparent"
              >
                New Interviewer
              </Button>
            )
          }
        />
        {children}
      </div>
      <ImpersonationBanner />
    </div>
  );
}
